'use client'

// NB-F195: Cloudflare Turnstile CAPTCHA on auth surfaces.
//
// Why Turnstile (vs reCAPTCHA / hCaptcha):
//   - Free, no per-request billing
//   - GDPR-friendly: no Google FLoC / cross-site tracking cookies
//   - Generally invisible to legitimate users (no image-grid challenges)
//   - First-party Cloudflare integration, sensible no-op when site key
//     unset so non-prod environments degrade gracefully
//
// Render contract:
//   - If NEXT_PUBLIC_TURNSTILE_SITE_KEY is not set, the widget renders
//     null and onVerify is called with an empty string so the parent
//     form can submit unchanged. This keeps preview / dev / staging
//     working before the owner provisions a Cloudflare site key.
//   - On success, parent receives the verification token to forward to
//     the backend (e.g. as the `cf-turnstile-response` field or header).
//   - On EXPIRE, parent receives empty string so it re-blocks submit
//     until the widget self-renews. The widget is healthy in that case,
//     so re-gating is safe.
//   - On ERROR (v137 fail-open), parent receives empty string AND
//     onErrorStateChange(true). Error means the widget itself is broken:
//     invalid/mismatched site key, Cloudflare outage, or an adblocker
//     eating the challenge script. If parents kept gating submit on a
//     token in that state, login/signup/forgot-password would be
//     hard-bricked client-side even though the SERVER is the real
//     enforcement point (log_only today; in enforce mode the server
//     challenges and returns an actionable error). Parents must treat
//     widgetFailed=true as permission to submit without a token and let
//     the server decide. Client-side hard-brick is always worse.

import { useEffect, useRef, useState } from 'react'
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile'
import { HelpCircle, ShieldCheck } from 'lucide-react'

export const TURNSTILE_SITE_KEY =
  process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || ''

export interface TurnstileWidgetProps {
  /**
   * Called whenever the verification token changes. Receives the token
   * on success, or '' on expire / error so the parent can disable submit.
   */
  onVerify: (token: string) => void
  /**
   * v137 fail-open contract. Called with `true` when the widget ERRORS
   * (bad site key, Cloudflare outage, adblocker) and with `false` when a
   * later success proves it recovered. Parents use this to allow submit
   * WITHOUT a token while the widget is broken, so the server (the real
   * enforcement point) decides instead of a client-side hard-brick.
   * Expire does NOT flip this flag: an expired token on a healthy widget
   * keeps the normal re-gate behavior.
   */
  onErrorStateChange?: (widgetFailed: boolean) => void
  /**
   * Cosmetic action label sent to Cloudflare so distinct surfaces show
   * up separately in the Turnstile analytics dashboard.
   */
  action?: 'login' | 'signup' | 'forgot-password'
  /** Optional explicit theme; defaults to 'light' to match cream UI. */
  theme?: 'light' | 'dark' | 'auto'
  /** Optional id for aria associations from a form-level error region. */
  id?: string
  className?: string
}

/**
 * Shared Turnstile widget used by every auth form on PulseSignal.
 *
 * Rendering side-effects:
 *   - Calls onVerify('') once on mount when no site key is present, so
 *     the parent's submit-disabled gate (which checks for empty token)
 *     can be wired in a single place without conditional branching.
 *   - Calls onVerify('') on expire so we re-block submit and Turnstile
 *     auto-renews. Cloudflare tokens are 5-minute scoped: if the user
 *     stops typing on the signup form for 5+ minutes, the token will
 *     expire and the widget self-renews; we just need to keep the
 *     parent's "submit allowed?" flag in sync.
 */
export default function TurnstileWidget({
  onVerify,
  onErrorStateChange,
  action,
  theme = 'light',
  id,
  className,
}: TurnstileWidgetProps) {
  const ref = useRef<TurnstileInstance | null>(null)
  const [showWhy, setShowWhy] = useState(false)
  // True after onError fires: the widget itself is broken (not just an
  // expired token), so parents fail open and submit without a token.
  const [widgetFailed, setWidgetFailed] = useState(false)

  // Site-key absence: graceful no-op so the owner can ship the rest of
  // the auth flow before provisioning Turnstile in Cloudflare.
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) {
      onVerify('')
    }
    // We intentionally do NOT depend on onVerify to avoid re-firing if
    // the parent passes a new function identity each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!TURNSTILE_SITE_KEY) {
    // Render nothing in environments where the site key is not set.
    // Parent forms gate submit on a non-empty token only when the env
    // var is present (see `requireCaptcha` pattern in each page).
    return null
  }

  return (
    <div className={className}>
      <div
        className="flex flex-col items-start gap-2"
        // aria-live so screen-readers hear the verification state without
        // hijacking focus. Errors below also use aria-live=polite.
      >
        <Turnstile
          ref={ref}
          siteKey={TURNSTILE_SITE_KEY}
          options={{
            action,
            theme,
            // appearance:'interaction-only' keeps the widget hidden for
            // human visitors and only shows a challenge when Cloudflare
            // judges the request risky. Better UX than 'always'.
            appearance: 'interaction-only',
            // Match form width on mobile; flexible at desktop.
            size: 'flexible',
          }}
          id={id}
          onSuccess={(token) => {
            // A success after an earlier error means the widget recovered
            // (e.g. transient Cloudflare blip). Drop the fail-open state so
            // the normal token gate applies again.
            setWidgetFailed(false)
            onErrorStateChange?.(false)
            onVerify(token)
          }}
          onExpire={() => {
            // EXPIRE: widget is healthy, token just aged out (5min TTL).
            // Keep the strict re-gate: clear token, submit stays disabled
            // until the self-renew produces a fresh one. Do NOT fail open.
            onVerify('')
            // Self-renew so the user doesn't have to do anything.
            ref.current?.reset()
          }}
          onError={() => {
            // ERROR: the widget is broken (invalid site key, CF outage,
            // adblocker). @marsidev/react-turnstile retries retryable
            // errors itself before this fires. FAIL OPEN: clear the token
            // and tell the parent so it allows submit without one. The
            // server verifies anyway (log_only today, challenge in
            // enforce mode), so the user is never bricked client-side.
            onVerify('')
            setWidgetFailed(true)
            onErrorStateChange?.(true)
          }}
        />
        {widgetFailed && (
          <p
            role="status"
            aria-live="polite"
            className="text-caption text-ink-600"
          >
            The security check could not load, possibly because of an ad
            blocker or a network hiccup. You can continue without it.
          </p>
        )}
        <button
          type="button"
          onClick={() => setShowWhy((v) => !v)}
          className="inline-flex items-center gap-1 text-caption text-ink-600 hover:text-terra-600 transition-colors"
          aria-expanded={showWhy}
          aria-controls={id ? `${id}-why` : 'turnstile-why'}
        >
          <HelpCircle className="w-3 h-3" aria-hidden />
          Why this verification?
        </button>
        {showWhy && (
          <div
            id={id ? `${id}-why` : 'turnstile-why'}
            role="note"
            className="flex items-start gap-2 text-caption text-ink-700 bg-cream-50 border border-ink-900/10 rounded-lg p-3 max-w-sm"
          >
            <ShieldCheck className="w-3.5 h-3.5 text-olive-600 shrink-0 mt-0.5" aria-hidden />
            <span>
              We use Cloudflare Turnstile to block automated credential-stuffing
              attacks. It is invisible for most visitors, runs without tracking
              cookies, and never sees your password.
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * True when the site key env var is configured AND the parent should
 * require a non-empty token before submitting. Pages use this to gate
 * the submit button without having to duplicate the env-var check.
 */
export const isCaptchaRequired = (): boolean => Boolean(TURNSTILE_SITE_KEY)
